/**
 * Autodesk OAuth token service: PKCE login, code exchange, refresh, storage.
 * No client_secret; tokens in memory + sessionStorage only (no localStorage).
 */
import { generateCodeVerifier, generateCodeChallenge, generateState } from "./pkce";
import {
  AUTODESK_AUTHORIZE_URL,
  AUTODESK_TOKEN_URL,
  CLIENT_ID,
  REDIRECT_URI,
  SCOPES,
  STORAGE_KEYS,
} from "./config";

const EXPIRY_BUFFER_MS = 5 * 60 * 1000; // 5 minutes

/** In-memory token cache (single source of truth after hydration) */
let tokens = null;

function getSessionStorage() {
  if (typeof window === "undefined" || !window.sessionStorage) return null;
  return window.sessionStorage;
}

/**
 * Persist tokens to sessionStorage for survival across refresh (optional).
 * @param {Object} t - { access_token, refresh_token, expires_in }
 */
function persistTokens(t) {
  const ss = getSessionStorage();
  if (!ss) return;
  const expiresAt = t.expires_in
    ? Date.now() + t.expires_in * 1000
    : null;
  try {
    ss.setItem(
      STORAGE_KEYS.TOKENS,
      JSON.stringify({
        access_token: t.access_token,
        refresh_token: t.refresh_token || null,
        expires_in: t.expires_in,
        expires_at: expiresAt,
      })
    );
  } catch {
    // sessionStorage full or disabled
  }
}

/**
 * Load tokens from sessionStorage into memory (call once on app init).
 */
function hydrateFromSession() {
  if (tokens) return;
  const ss = getSessionStorage();
  if (!ss) return;
  try {
    const raw = ss.getItem(STORAGE_KEYS.TOKENS);
    if (raw) {
      const parsed = JSON.parse(raw);
      tokens = {
        access_token: parsed.access_token,
        refresh_token: parsed.refresh_token,
        expires_in: parsed.expires_in,
        expires_at: parsed.expires_at,
      };
    }
  } catch {
    // ignore
  }
}

/**
 * Get stored tokens (from memory, or hydrate from sessionStorage once).
 * @returns {{ access_token: string, refresh_token?: string, expires_in?: number, expires_at?: number } | null}
 */
export function getStoredTokens() {
  hydrateFromSession();
  return tokens;
}

/**
 * Check if current access token is still valid (with buffer).
 * @returns {boolean}
 */
export function hasValidAccessToken() {
  const t = getStoredTokens();
  if (!t || !t.access_token) return false;
  const expiresAt = t.expires_at;
  if (!expiresAt) return true;
  return Date.now() + EXPIRY_BUFFER_MS < expiresAt;
}

/**
 * Start Autodesk login: generate PKCE, store in sessionStorage, redirect to authorize.
 * No client_secret. Uses response_type=code and code_challenge_method=S256.
 */
export async function loginWithAutodesk() {
  const verifier = generateCodeVerifier();
  const state = generateState();
  const codeChallenge = await generateCodeChallenge(verifier);

  const ss = getSessionStorage();
  if (ss) {
    ss.setItem(STORAGE_KEYS.CODE_VERIFIER, verifier);
    ss.setItem(STORAGE_KEYS.STATE, state);
  }

  const params = new URLSearchParams({
    response_type: "code",
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: SCOPES,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    state,
  });
  const url = `${AUTODESK_AUTHORIZE_URL}?${params.toString()}`;
  window.location.replace(url);
}

/**
 * Exchange authorization code for tokens (callback). Validates state; no client_secret.
 * @param {string} code - Authorization code from query.
 * @param {string} stateFromUrl - state from query.
 * @returns {Promise<{ access_token: string, refresh_token?: string, expires_in?: number }>}
 */
export async function exchangeCodeForTokens(code, stateFromUrl) {
  const ss = getSessionStorage();
  const storedState = ss ? ss.getItem(STORAGE_KEYS.STATE) : null;
  const verifier = ss ? ss.getItem(STORAGE_KEYS.CODE_VERIFIER) : null;

  if (!storedState || storedState !== stateFromUrl) {
    clearPkce();
    throw new Error("Invalid state");
  }
  if (!verifier) {
    clearPkce();
    throw new Error("Missing code_verifier");
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    code_verifier: verifier,
  });

  const response = await fetch(AUTODESK_TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!response.ok) {
    const errText = await response.text();
    clearPkce();
    let errMessage = errText || `Token exchange failed: ${response.status}`;
    try {
      const errJson = JSON.parse(errText);
      if (errJson.error_description) errMessage = errJson.error_description;
      else if (errJson.error) errMessage = `${errJson.error}: ${errJson.error_description || ""}`.trim();
    } catch {
      // use errText as-is
    }
    if (response.status === 401 && /client credentials are invalid/i.test(errMessage)) {
      errMessage += " Use an APS app of type 'Desktop, Mobile, Single-Page Web App' (PKCE), and ensure REACT_APP_AUTODESK_CLIENT_ID matches that app.";
    }
    throw new Error(errMessage);
  }

  const data = await response.json();
  const result = {
    access_token: data.access_token,
    refresh_token: data.refresh_token ?? null,
    expires_in: data.expires_in ?? null,
  };
  if (result.expires_in) {
    result.expires_at = Date.now() + result.expires_in * 1000;
  }

  tokens = result;
  persistTokens(result);
  clearPkce();
  return result;
}

/**
 * Clear PKCE values from sessionStorage (after exchange or on error).
 */
function clearPkce() {
  const ss = getSessionStorage();
  if (ss) {
    ss.removeItem(STORAGE_KEYS.CODE_VERIFIER);
    ss.removeItem(STORAGE_KEYS.STATE);
  }
}

/**
 * Refresh access token using refresh_token. No client_secret.
 * On failure clears auth and throws.
 * @returns {Promise<{ access_token: string, refresh_token?: string, expires_in?: number }>}
 */
export async function refreshAccessToken() {
  const t = getStoredTokens();
  if (!t || !t.refresh_token) {
    clearAuth();
    throw new Error("No refresh token");
  }

  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: CLIENT_ID,
    refresh_token: t.refresh_token,
  });

  const response = await fetch(AUTODESK_TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!response.ok) {
    clearAuth();
    throw new Error(`Refresh failed: ${response.status}`);
  }

  const data = await response.json();
  const result = {
    access_token: data.access_token,
    refresh_token: data.refresh_token ?? t.refresh_token,
    expires_in: data.expires_in ?? null,
  };
  if (result.expires_in) {
    result.expires_at = Date.now() + result.expires_in * 1000;
  }

  tokens = result;
  persistTokens(result);
  return result;
}

/**
 * Clear all Autodesk auth state (tokens + PKCE) from memory and sessionStorage.
 */
export function clearAuth() {
  tokens = null;
  const ss = getSessionStorage();
  if (ss) {
    ss.removeItem(STORAGE_KEYS.TOKENS);
    ss.removeItem(STORAGE_KEYS.CODE_VERIFIER);
    ss.removeItem(STORAGE_KEYS.STATE);
  }
}
